/**
 * Per-browser preferences of the app shell: color theme and desktop sidebar. They live in
 * localStorage (instant, no backend); the storage can be missing or throw (private window,
 * blocked site data), in which case the defaults apply and nothing is remembered.
 */
export type Theme = 'dark' | 'light';
export type SidebarMode = 'expanded' | 'collapsed';

export const THEME_KEY = 'lombok.theme';
export const SIDEBAR_KEY = 'lombok.sidebar';

/** Dark is the default until the user picks the light theme. */
export const DEFAULT_THEME: Theme = 'dark';
export const DEFAULT_SIDEBAR: SidebarMode = 'expanded';

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;

export function parseTheme(value: unknown): Theme {
  return value === 'light' || value === 'dark' ? value : DEFAULT_THEME;
}

export function parseSidebar(value: unknown): SidebarMode {
  return value === 'collapsed' || value === 'expanded' ? value : DEFAULT_SIDEBAR;
}

function safeGet(key: string, storage?: ReadableStorage): string | null {
  try {
    return (storage ?? localStorage).getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string, storage?: WritableStorage): void {
  try {
    (storage ?? localStorage).setItem(key, value);
  } catch {
    /* not remembered, the screen still works */
  }
}

export const readTheme = (storage?: ReadableStorage): Theme => parseTheme(safeGet(THEME_KEY, storage));
export const saveTheme = (theme: Theme, storage?: WritableStorage): void => safeSet(THEME_KEY, theme, storage);
export const readSidebar = (storage?: ReadableStorage): SidebarMode => parseSidebar(safeGet(SIDEBAR_KEY, storage));
export const saveSidebar = (mode: SidebarMode, storage?: WritableStorage): void => safeSet(SIDEBAR_KEY, mode, storage);

/** First letters of the first two words of a name ("María Pérez" -> "MP"); "?" when empty. */
export function initialsOf(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase());
  return letters.join('') || '?';
}

/** True for the signed-in app (`/app` and everything under it); the public pages are not. */
export function isAppPath(pathname: string): boolean {
  return pathname === '/app' || pathname.startsWith('/app/');
}
